import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Megaphone, X, Send, Building2, User, Mail, Phone, Globe, MessageSquare, DollarSign, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from '../hooks/use-toast';

const HomepageAdSidebar = ({ isMobile = false }) => {
  const [advertisements, setAdvertisements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [contactFormData, setContactFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    website: '',
    message: '',
    ad_type: 'banner',
    budget_range: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchAdvertisements();
  }, []);

  const fetchAdvertisements = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/advertisements`);
      if (response.ok) {
        const data = await response.json();
        // Filter to only show sidebar ads (exclude banner ads)
        const sidebarAds = (data.advertisements || []).filter(ad => ad.ad_type !== 'banner');
        setAdvertisements(sidebarAds);
        
        // Track impressions for each sidebar ad
        sidebarAds.forEach(ad => {
          fetch(`${API_BASE}/api/advertisements/${ad.id}/impression`, { method: 'POST' });
        });
      }
    } catch (error) {
      console.error('Error fetching advertisements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdClick = async (ad) => {
    // Track click
    try {
      await fetch(`${API_BASE}/api/advertisements/${ad.id}/click`, { method: 'POST' });
    } catch (error) {
      console.error('Error tracking click:', error);
    }
    
    // Open link
    if (ad.link_url) {
      window.open(ad.link_url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await fetch(`${API_BASE}/api/advertising/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactFormData)
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: 'Inquiry Submitted! 🎉',
          description: 'Thank you for your interest. We will contact you within 24-48 hours.',
        });
        setIsContactModalOpen(false);
        setContactFormData({
          company_name: '',
          contact_name: '',
          email: '',
          phone: '',
          website: '',
          message: '',
          ad_type: 'banner',
          budget_range: ''
        });
      } else {
        toast({
          title: 'Error',
          description: data.detail || 'Failed to submit inquiry',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to submit inquiry. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactFormData(prev => ({ ...prev, [name]: value }));
  };

  // If no active ads, show placeholder
  if (loading) {
    return (
      <div className="space-y-4">
        <div className="bg-gray-100 dark:bg-gray-800 rounded-xl p-6 animate-pulse">
          <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={isMobile ? "space-y-4" : "space-y-4"}>
        {/* Section Title */}
        <div className="text-center mb-2">
          <span className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider">Sponsored</span>
        </div>

        {/* Active Advertisements - Grid layout for mobile */}
        {advertisements.length > 0 ? (
          <div className={isMobile ? "grid grid-cols-1 sm:grid-cols-2 gap-4" : "space-y-4"}>
            {advertisements.map((ad) => {
              return (
                <div
                  key={ad.id}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer group"
                  onClick={() => handleAdClick(ad)}
                >
                  {ad.image_url && (
                    <div className="w-full h-48 bg-white dark:bg-gray-700 overflow-hidden flex items-center justify-center p-3">
                      <img
                        src={ad.image_url.startsWith('/uploads/') ? ad.image_url : (ad.image_url.startsWith('/') ? `${API_BASE}${ad.image_url}` : ad.image_url)}
                        alt={ad.title}
                        className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.src = '/trainerlist-logo.png';
                        }}
                      />
                    </div>
                  )}
                  <div className="p-4">
                  <h3 className="font-bold text-gray-900 dark:text-white mb-1 group-hover:text-red-600 transition-colors">
                    {ad.title}
                  </h3>
                  {ad.company_name && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      by {ad.company_name}
                    </p>
                )}
                {ad.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 line-clamp-2">
                    {ad.description}
                  </p>
                )}
                {ad.link_url && (
                  <Button
                    size="sm"
                    className="w-full bg-red-600 hover:bg-red-700 text-white"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAdClick(ad);
                    }}
                  >
                    {ad.link_text || 'Learn More'}
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </div>
            );
          })}
          </div>
        ) : null}

        {/* Want to Advertise Box - Always show */}
        <div className={isMobile ? "mt-4" : ""}>
          <div 
            className="bg-gradient-to-br from-amber-50 to-orange-100 dark:from-amber-900/30 dark:to-orange-900/30 border-2 border-dashed border-amber-300 dark:border-amber-700 rounded-xl p-5 cursor-pointer hover:shadow-lg transition-all duration-300 group"
            onClick={() => setIsContactModalOpen(true)}
          >
            <div className={`flex ${isMobile ? 'flex-row items-center gap-4' : 'flex-col items-center text-center space-y-3'}`}>
              <div className={`p-3 rounded-full bg-amber-200 dark:bg-amber-800 group-hover:scale-110 transition-transform ${isMobile ? 'flex-shrink-0' : ''}`}>
                <Megaphone className={`${isMobile ? 'w-6 h-6' : 'w-7 h-7'} text-amber-600 dark:text-amber-400`} />
              </div>
              <div className={isMobile ? 'flex-1' : ''}>
                <h3 className="font-bold text-amber-800 dark:text-amber-200">
                  Advertise With Us
                </h3>
                <p className="text-sm mt-1 text-amber-600 dark:text-amber-400">
                  Reach thousands of fitness enthusiasts
                </p>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                className={`border-amber-400 text-amber-700 hover:bg-amber-100 dark:border-amber-600 dark:text-amber-300 dark:hover:bg-amber-900 ${isMobile ? 'flex-shrink-0' : 'mt-2'}`}
              >
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Modal - Rendered via Portal to ensure proper z-index */}
      {isContactModalOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-600 to-red-700 text-white p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Megaphone className="w-8 h-8" />
                  <div>
                    <h2 className="text-xl font-bold">Advertise on TrainerList</h2>
                    <p className="text-red-100 text-sm">Connect with fitness professionals & enthusiasts</p>
                  </div>
                </div>
                <button 
                  onClick={() => setIsContactModalOpen(false)}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleContactSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-1">
                    <Building2 className="w-4 h-4" /> Company Name *
                  </label>
                  <Input
                    name="company_name"
                    value={contactFormData.company_name}
                    onChange={handleContactChange}
                    placeholder="Your company name"
                    required
                    className="dark:bg-gray-700"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-1">
                    <User className="w-4 h-4" /> Contact Name *
                  </label>
                  <Input
                    name="contact_name"
                    value={contactFormData.contact_name}
                    onChange={handleContactChange}
                    placeholder="Your name"
                    required
                    className="dark:bg-gray-700"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-1">
                    <Mail className="w-4 h-4" /> Email *
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={contactFormData.email}
                    onChange={handleContactChange}
                    placeholder="your@email.com"
                    required
                    className="dark:bg-gray-700"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-1">
                    <Phone className="w-4 h-4" /> Phone
                  </label>
                  <Input
                    name="phone"
                    value={contactFormData.phone}
                    onChange={handleContactChange}
                    placeholder="+1 (555) 000-0000"
                    className="dark:bg-gray-700"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-1">
                  <Globe className="w-4 h-4" /> Website
                </label>
                <Input
                  name="website"
                  value={contactFormData.website}
                  onChange={handleContactChange}
                  placeholder="https://yourwebsite.com"
                  className="dark:bg-gray-700"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                    Ad Type
                  </label>
                  <select
                    name="ad_type"
                    value={contactFormData.ad_type}
                    onChange={handleContactChange}
                    className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="banner">Banner Ad</option>
                    <option value="sponsored">Sponsored Content</option>
                    <option value="featured">Featured Listing</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-1">
                    <DollarSign className="w-4 h-4" /> Budget Range
                  </label>
                  <select
                    name="budget_range"
                    value={contactFormData.budget_range}
                    onChange={handleContactChange}
                    className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="">Select budget...</option>
                    <option value="under_500">Under $500/month</option>
                    <option value="500_1000">$500 - $1,000/month</option>
                    <option value="1000_2500">$1,000 - $2,500/month</option>
                    <option value="2500_5000">$2,500 - $5,000/month</option>
                    <option value="over_5000">Over $5,000/month</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-1">
                  <MessageSquare className="w-4 h-4" /> Message *
                </label>
                <Textarea
                  name="message"
                  value={contactFormData.message}
                  onChange={handleContactChange}
                  placeholder="Tell us about your advertising goals..."
                  rows={4}
                  required
                  className="dark:bg-gray-700"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsContactModalOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  {submitting ? 'Sending...' : (
                    <><Send className="w-4 h-4 mr-2" /> Send Inquiry</>
                  )}
                </Button>
              </div>

              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                We typically respond within 24-48 business hours
              </p>
            </form>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default HomepageAdSidebar;
